/**
 * Text-to-Speech utility using browser's Web Speech API.
 * Handles markdown stripping, language detection, and iOS Safari chunking quirks.
 * Falls back to server-side OpenAI TTS when window.speechSynthesis is unavailable
 * (e.g. Android WebView in the wrapped mobile app).
 */
import api from '../services/api';

// Strip markdown / formatting characters so speech sounds natural
export function cleanForSpeech(text) {
  if (!text) return '';
  let cleaned = text;

  // Remove markdown tables entirely (they sound awful when spoken)
  cleaned = cleaned.replace(/\|[^\n]*\|/g, '');
  cleaned = cleaned.replace(/^[\s|:-]+$/gm, '');

  // Remove markdown headers (## Heading -> Heading)
  cleaned = cleaned.replace(/^#{1,6}\s+/gm, '');

  // Remove bold/italic markers
  cleaned = cleaned.replace(/\*{1,3}([^*]+)\*{1,3}/g, '$1');
  cleaned = cleaned.replace(/_{1,3}([^_]+)_{1,3}/g, '$1');

  // Remove inline code backticks
  cleaned = cleaned.replace(/`([^`]+)`/g, '$1');
  cleaned = cleaned.replace(/```[\s\S]*?```/g, '');

  // Remove links: [text](url) -> text
  cleaned = cleaned.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');

  // Strip emojis (broadly — keep alphanumerics, common punctuation, spaces, basic symbols)
  cleaned = cleaned.replace(/[\u{1F300}-\u{1FAFF}]|[\u{2600}-\u{27BF}]|[\u{1F000}-\u{1F2FF}]|[\u{1F600}-\u{1F64F}]|[\u{1F680}-\u{1F6FF}]/gu, '');

  // Remove arrows/dashes used as bullet decoration
  cleaned = cleaned.replace(/→/g, ' to ');
  cleaned = cleaned.replace(/^\s*[-•*]\s+/gm, '');

  // Collapse multiple whitespace/newlines
  cleaned = cleaned.replace(/\n{2,}/g, '. ');
  cleaned = cleaned.replace(/\s+/g, ' ').trim();

  return cleaned;
}

// Detect language from text — returns BCP47 language tag.
// Counts characters in each Indian script + Latin, and picks the dominant one so
// a mostly-English response with one Hindi word still speaks in English.
export function detectLanguage(text) {
  if (!text) return 'en-IN';

  // Unicode ranges for major Indian scripts
  const ranges = [
    { lang: 'hi-IN', re: /[ऀ-ॿ]/g }, // Devanagari (Hindi, Marathi, Sanskrit)
    { lang: 'bn-IN', re: /[ঀ-৿]/g }, // Bengali
    { lang: 'pa-IN', re: /[਀-੿]/g }, // Gurmukhi (Punjabi)
    { lang: 'gu-IN', re: /[઀-૿]/g }, // Gujarati
    { lang: 'or-IN', re: /[଀-୿]/g }, // Odia
    { lang: 'ta-IN', re: /[஀-௿]/g }, // Tamil
    { lang: 'te-IN', re: /[ఀ-౿]/g }, // Telugu
    { lang: 'kn-IN', re: /[ಀ-೿]/g }, // Kannada
    { lang: 'ml-IN', re: /[ഀ-ൿ]/g }, // Malayalam
  ];

  // Count how many characters of each script appear
  let best = { lang: 'en-IN', count: 0 };
  for (const r of ranges) {
    const matches = text.match(r.re);
    const count = matches ? matches.length : 0;
    if (count > best.count) best = { lang: r.lang, count };
  }

  // Only switch away from English if the Indian script is clearly dominant
  // (>15% of text length) — a single Hindi word inside English shouldn't flip lang.
  if (best.count > 0 && best.count / text.length > 0.15) return best.lang;
  return 'en-IN';
}

// Detect language for a SHORT chunk — more sensitive threshold so mixed responses
// speak Hindi sentences in Hindi and English sentences in English.
function detectChunkLanguage(chunk) {
  if (!chunk) return 'en-IN';
  const ranges = [
    { lang: 'hi-IN', re: /[ऀ-ॿ]/ },
    { lang: 'bn-IN', re: /[ঀ-৿]/ },
    { lang: 'pa-IN', re: /[਀-੿]/ },
    { lang: 'gu-IN', re: /[઀-૿]/ },
    { lang: 'or-IN', re: /[଀-୿]/ },
    { lang: 'ta-IN', re: /[஀-௿]/ },
    { lang: 'te-IN', re: /[ఀ-౿]/ },
    { lang: 'kn-IN', re: /[ಀ-೿]/ },
    { lang: 'ml-IN', re: /[ഀ-ൿ]/ },
  ];
  for (const r of ranges) if (r.re.test(chunk)) return r.lang;
  return 'en-IN';
}

// Chunk long text into ~250 char sentences for iOS Safari
function chunkText(text, maxChars = 250) {
  if (text.length <= maxChars) return [text];
  const chunks = [];
  const sentences = text.split(/(?<=[.!?])\s+/);
  let current = '';
  for (const s of sentences) {
    if ((current + ' ' + s).length > maxChars && current) {
      chunks.push(current.trim());
      current = s;
    } else {
      current = current ? current + ' ' + s : s;
    }
  }
  if (current.trim()) chunks.push(current.trim());
  return chunks;
}

// Pick a preferred voice for a language (e.g., Indian English voice if available)
function pickVoice(lang) {
  const voices = window.speechSynthesis.getVoices();
  if (!voices.length) return null;
  // Prefer exact lang match (en-IN), then language family (en), then any
  return (
    voices.find(v => v.lang === lang) ||
    voices.find(v => v.lang.startsWith(lang.split('-')[0])) ||
    voices[0]
  );
}

let currentChunks = [];
let currentIdx = 0;
let currentOnEnd = null;
let isSpeaking = false;
let isPaused = false;

// Server-fallback playback state (when window.speechSynthesis is unavailable, e.g. some Android WebViews).
let serverAudio = null;     // HTMLAudioElement currently playing
let serverAudioUrl = null;  // blob: URL to revoke on stop

function speakNextChunk(lang, rate) {
  if (currentIdx >= currentChunks.length) {
    isSpeaking = false;
    isPaused = false;
    if (currentOnEnd) currentOnEnd();
    return;
  }
  // Detect language per chunk so a mixed English + Hindi response speaks each
  // chunk in its own language (fall back to the overall-detected lang if no
  // Indian script found in this chunk).
  const chunkText = currentChunks[currentIdx];
  const chunkLang = detectChunkLanguage(chunkText) !== 'en-IN'
    ? detectChunkLanguage(chunkText)
    : lang;
  const utterance = new SpeechSynthesisUtterance(chunkText);
  utterance.lang = chunkLang;
  utterance.rate = rate;
  utterance.pitch = 1.0;
  const voice = pickVoice(chunkLang);
  if (voice) utterance.voice = voice;
  utterance.onend = () => {
    currentIdx++;
    speakNextChunk(lang, rate);
  };
  utterance.onerror = () => {
    currentIdx++;
    speakNextChunk(lang, rate);
  };
  window.speechSynthesis.speak(utterance);
}

// Fallback: ask the server to generate audio (OpenAI TTS) and play it.
// Used when window.speechSynthesis is unavailable — e.g. Android WebView in the wrapped mobile app.
// Uses the shared `api` axios instance so the Authorization header (set by authStore) is included.
async function speakViaServer(cleanedText, { onEnd } = {}) {
  try {
    const res = await api.post(
      '/chat/tts',
      { text: cleanedText.slice(0, 4000) },
      { responseType: 'blob' }
    );
    const blob = res.data;
    serverAudioUrl = URL.createObjectURL(blob);
    serverAudio = new Audio(serverAudioUrl);
    isSpeaking = true;
    isPaused = false;
    serverAudio.onended = () => {
      isSpeaking = false;
      isPaused = false;
      if (serverAudioUrl) { URL.revokeObjectURL(serverAudioUrl); serverAudioUrl = null; }
      serverAudio = null;
      if (onEnd) onEnd();
    };
    serverAudio.onerror = () => {
      isSpeaking = false;
      isPaused = false;
      if (serverAudioUrl) { URL.revokeObjectURL(serverAudioUrl); serverAudioUrl = null; }
      serverAudio = null;
      if (onEnd) onEnd();
    };
    await serverAudio.play();
    return true;
  } catch (e) {
    console.warn('Server TTS error:', e.response?.status, e.message);
    isSpeaking = false;
    isPaused = false;
    if (onEnd) onEnd();
    return false;
  }
}

export function speak(text, { rate = 1.0, onEnd } = {}) {
  stop(); // cancel any existing
  const cleaned = cleanForSpeech(text);
  if (!cleaned) return false;

  // No browser speechSynthesis (e.g. Android WebView) → server TTS fallback.
  if (!('speechSynthesis' in window)) {
    currentOnEnd = onEnd;
    speakViaServer(cleaned, { onEnd });
    return true;
  }

  const lang = detectLanguage(cleaned);
  currentChunks = chunkText(cleaned);
  currentIdx = 0;
  currentOnEnd = onEnd;
  isSpeaking = true;

  // iOS Safari workaround: voices load asynchronously; if voices aren't ready, wait once
  if (!window.speechSynthesis.getVoices().length) {
    window.speechSynthesis.onvoiceschanged = () => {
      window.speechSynthesis.onvoiceschanged = null;
      speakNextChunk(lang, rate);
    };
  } else {
    speakNextChunk(lang, rate);
  }
  return true;
}

export function pause() {
  if (serverAudio && isSpeaking && !isPaused) {
    serverAudio.pause();
    isPaused = true;
    return true;
  }
  if ('speechSynthesis' in window && isSpeaking) {
    window.speechSynthesis.pause();
    isPaused = true;
    return true;
  }
  return false;
}

export function resume() {
  if (serverAudio && isPaused) {
    serverAudio.play();
    isPaused = false;
    return true;
  }
  if ('speechSynthesis' in window && isPaused) {
    window.speechSynthesis.resume();
    isPaused = false;
    return true;
  }
  return false;
}

export function isCurrentlyPaused() {
  return isPaused;
}

export function stop() {
  if ('speechSynthesis' in window) {
    window.speechSynthesis.cancel();
  }
  if (serverAudio) {
    try { serverAudio.pause(); } catch (e) {}
    serverAudio = null;
  }
  if (serverAudioUrl) {
    URL.revokeObjectURL(serverAudioUrl);
    serverAudioUrl = null;
  }
  currentChunks = [];
  currentIdx = 0;
  isSpeaking = false;
  isPaused = false;
  if (currentOnEnd) {
    const cb = currentOnEnd;
    currentOnEnd = null;
    cb();
  }
}

// Always true now — we have a server fallback for environments where
// window.speechSynthesis is missing (e.g. Android WebView).
export function isSpeechSupported() {
  return true;
}

// Read just the user's text selection inside an element, if any
export function getSelectedTextWithin(element) {
  if (!element || typeof window === 'undefined') return '';
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0 || selection.isCollapsed) return '';
  const range = selection.getRangeAt(0);
  // Check if the selection is within (or intersects) the given element
  if (!element.contains(range.commonAncestorContainer) &&
      !element.contains(range.startContainer) &&
      !element.contains(range.endContainer)) {
    return '';
  }
  return selection.toString().trim();
}
